import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery, useQueryClient } from 'react-query'
import { AIJobStatus, BrandVisualEnhancement } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { cacheKeys } from './cacheKeys'

interface Data {
  brand_visual_enhancement: BrandVisualEnhancement
}

export const fetchBrandVisualEnhancement = async ({
  id,
  brandVisualKitId,
  workspaceId,
  signal,
}: {
  id: number
  brandVisualKitId: number
  workspaceId: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(
    `/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_visual_enhancements/${id}`,
    { signal },
  )

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data
}

/**
 * React‑Query hook to retrieve & (optionally) poll a Brand Visual Enhancement until it
 * reaches a terminal AIJobStatus (Successful | Failed | Expired).
 */
export const useBrandVisualEnhancement = (
  brandVisualKitId: number | undefined,
  id: number | undefined,
  options?: UseQueryOptions<Data> & {
    /**
     * Set to `true` to poll every 500 ms until the job reaches a terminal status.
     * Default is `false` (no polling).
     */
    pollUntilFinal?: boolean
  },
) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const { pollUntilFinal = false, ...queryOptions } = options ?? {}

  const pollingInterval = (data: Data | undefined) => {
    if (!pollUntilFinal) return false
    if (!data) return 500
    const status = data.brand_visual_enhancement.status as AIJobStatus
    return [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired].includes(status) ? false : 500
  }

  return useQuery<Data>(
    cacheKeys.detail(workspaceId, brandVisualKitId ?? 0, id),
    () => {
      if (!id || !brandVisualKitId) {
        throw new Error('Both brandVisualKitId and id are required')
      }
      return fetchBrandVisualEnhancement({ id, brandVisualKitId, workspaceId })
    },
    {
      placeholderData: () => queryClient.getQueryData<Data>(cacheKeys.detail(workspaceId, brandVisualKitId ?? 0, id)),
      refetchInterval: queryOptions.refetchInterval ?? pollingInterval,
      refetchOnWindowFocus: false,
      staleTime: (queryOptions.cacheTime || 0) * 0.5,
      enabled: !!id && !!brandVisualKitId,
      ...queryOptions,
    },
  )
}
